import { BillingType, BILLING_TYPE_LABELS } from 'domain/shared/newProject';
import { useEffect, useMemo } from 'react';
import { DateInput, InputList, EnumDropdown, FormFieldsData, useFormContext } from 'framework/form';
import {
  INVOICE_DATE_BUSINESS_RULE,
  INVOICE_DATE_BUSINESS_RULE_LABELS,
  PERIOD_POLICY,
  PERIOD_POLICY_LABELS,
} from '../constant';
import BillingProjectPeriodicCalendar from '../shared/inputs/BillingProjectPeriodicCalendar';
import BillingProjectPeriodicCustom from '../shared/inputs/BillingProjectPeriodicCustom';
import {
  generateBillingPeriods,
  getBillingScheduleItemRemoveTooltip,
  getBillingScheduleItemsTotalAmount,
  getBillingScheduleTargetAmount,
  hasLockedBillingScheduleItems,
  isBillingScheduleItemRemovable,
} from '../helpers/projectBillingHelper';
import billingMilestonesValidationShema from './billingMilestonesValidationShema';
import billingPeriodicCalendarValidationShema from './billingPeriodicCalendarValidationShema';
import billingPeriodicCustomValidationShema from './billingPeriodicCustomValidationShema';
import { useShowContext } from 'framework/show';
import { Box, Typography, Grid2 as Grid } from '@mui/material';
import BillingProjectMilestonesInputs from '../shared/inputs/BillingProjectMilestonesInputs';
import { FloatingButtons } from 'framework/ui/ui-builder';

interface ProjectBillingFormProps {
  onCancelAction: () => void;
}

const ProjectBillingForm = ({ onCancelAction }: ProjectBillingFormProps) => {
  const { data } = useShowContext();
  const { data: formData, onChangeCallback, setValidationSchemaAction, setFormErrors } = useFormContext();
  const { amount = 0, currency, billingType } = data.getData();
  const { scheduleItems = [], periodPolicy, startDate, endDate, sowId, hasExistingBillingSchedule } = formData || {};

  const isMilestone = billingType === BillingType.MILESTONE;
  const hasLockedItems = hasLockedBillingScheduleItems(scheduleItems);

  const sum = useMemo(() => getBillingScheduleItemsTotalAmount(scheduleItems), [scheduleItems]);
  const targetAmount = useMemo(() => getBillingScheduleTargetAmount(amount), [amount]);

  const onChangePeriodType = (values: FormFieldsData) => {
    setFormErrors!({});
    onChangeCallback!({
      scheduleItems: generateBillingPeriods(values.periodPolicy, startDate, endDate),
      invoiceDateBusinessRule:
        values.periodPolicy === PERIOD_POLICY.CALENDAR_MONTH
          ? formData?.invoiceDateBusinessRule || INVOICE_DATE_BUSINESS_RULE.BY_1ST_BUSINESS_DAY
          : undefined,
      ...values,
    });
  };

  useEffect(() => {
    if (isMilestone) {
      setValidationSchemaAction!(billingMilestonesValidationShema);
    } else {
      setValidationSchemaAction!(
        periodPolicy === PERIOD_POLICY.CALENDAR_MONTH
          ? billingPeriodicCalendarValidationShema
          : billingPeriodicCustomValidationShema,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMilestone, periodPolicy]);

  return (
    <Box
      pt="5px"
      overflow="auto"
      sx={{
        '.floating-btns': {
          padding: '15px 0',
          m: 0,
        },
      }}
    >
      <Box minWidth="800px">
        <Grid container columnSpacing="60px" width="calc(100% - 46px)">
          <Grid size={3}>
            <EnumDropdown disabled source="billingType" label="Billing type" options={BILLING_TYPE_LABELS} />
          </Grid>
          {!isMilestone && (
            <>
              <Grid size={3}>
                <DateInput disabled source="startDate" label="Service start date" />
              </Grid>
              <Grid size={3}>
                <DateInput disabled source="endDate" label="Service end date" />
              </Grid>
              <Grid size={3}>
                <EnumDropdown
                  disabled={hasExistingBillingSchedule || hasLockedItems}
                  onChangeCallback={onChangePeriodType}
                  source="periodPolicy"
                  label="Period"
                  options={PERIOD_POLICY_LABELS}
                />
              </Grid>
              {periodPolicy === PERIOD_POLICY.CALENDAR_MONTH && !hasExistingBillingSchedule && (
                <Grid size={3}>
                  <EnumDropdown
                    source="invoiceDateBusinessRule"
                    label="Initial request deadline"
                    options={INVOICE_DATE_BUSINESS_RULE_LABELS}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Box
          sx={{
            '.removable-input': {
              mb: '0 !important',
              flexWrap: 'nowrap',
            },
          }}
        >
          <Typography variant="subtitle1" mb="20px">
            {isMilestone ? 'Milestones' : 'Periods'}
          </Typography>

          {isMilestone ? (
            <InputList
              inputTemplate={BillingProjectMilestonesInputs}
              source="scheduleItems"
              addText="Add milestone"
              maxFields={100}
              defaultValue={{ sowId }}
              disabledRemove={item => !isBillingScheduleItemRemovable(item as any)}
              removeButtonTooltipe={getBillingScheduleItemRemoveTooltip}
            />
          ) : periodPolicy === PERIOD_POLICY.CALENDAR_MONTH ? (
            <InputList
              inputTemplate={BillingProjectPeriodicCalendar}
              source="scheduleItems"
              addText="Add period"
              maxFields={100}
              defaultValue={{ sowId }}
              disabledRemove={item => !isBillingScheduleItemRemovable(item as any)}
              removeButtonTooltipe={getBillingScheduleItemRemoveTooltip}
            />
          ) : (
            <InputList
              inputTemplate={BillingProjectPeriodicCustom}
              source="scheduleItems"
              addText="Add period"
              maxFields={100}
              defaultValue={{ sowId }}
              disabledRemove={item => !isBillingScheduleItemRemovable(item as any)}
              removeButtonTooltipe={getBillingScheduleItemRemoveTooltip}
            />
          )}
        </Box>

        <Grid container columnSpacing="60px" width="calc(100% - 46px)">
          <Grid
            size={!isMilestone && periodPolicy === PERIOD_POLICY.CUSTOM_PERIOD ? 12 : 9}
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-end"
            flexDirection="column"
          >
            <Typography color="textSecondary">
              {Number(sum)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
              {currency}
            </Typography>
            <Typography component="span" display="flex" justifyContent="space-beetwen">
              <Typography component="span" mr="50px">
                SOW amount
              </Typography>
              <Typography color="textSecondary">
                {Number(targetAmount)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
                {currency}
              </Typography>
            </Typography>
          </Grid>
        </Grid>

        <FloatingButtons onCancelAction={onCancelAction} />
      </Box>
    </Box>
  );
};

export default ProjectBillingForm;
